import {
  ActionButton,
  Box,
  Button,
  CircularProgress,
  DetailPageHeader,
  HairlineCard,
  Icons,
  iconSize,
  Stack,
  StatusChip,
  statusTone,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { ContractStatus } from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { openContractDocument, useContract } from '../../lib/administration-api';
import { formatDate } from '../../lib/relative-time';

/** One label/value pair. Blank values read as an em dash, not an empty gap. */
function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 11.5,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14.5, color: value ? 'text.primary' : 'text.secondary' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

/**
 * One contract, read-only.
 *
 * A page rather than the dialog this used to be. A contract carries a term, the
 * council resolution behind it, the officers appointed under it and the copy
 * that was actually signed — and a modal a clerk has to scroll to reach the PDF
 * is the wrong shape for a record people come back to and read. It also had no
 * address, so there was no way to send a colleague *this contract*.
 */
export function ContractDetail() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useMutationToast();
  const { id } = useParams<{ id: string }>();
  const { active, can } = useScope();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : undefined;

  const contract = useContract(id, localGovernmentId);
  const row = contract.data;

  return (
    <AppShell>
      <Stack spacing={2.5}>
        <DetailPageHeader
          backLabel={t('common.actions.back')}
          onBack={() => navigate('/administration/contracts')}
          title={row?.companyName ?? t('gov.admin.tabs.contracts')}
          subtitle={row?.localGovernmentName}
          badge={
            row && (
              <StatusChip
                status={statusTone.contract[row.status]}
                label={t(`gov.admin.contracts.statuses.${row.status}`)}
              />
            )
          }
          actions={
            can('contract:draft') && row && row.status !== 'terminated' ? (
              <ActionButton
                label={t('common.actions.edit')}
                icon={Icons.Pencil}
                onClick={() => navigate(`/administration/contracts/${row.id}/edit`)}
              />
            ) : null
          }
        />

        {contract.isLoading && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!contract.isLoading && !row && (
          <HairlineCard sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('gov.admin.contracts.notFound')}
            </Typography>
          </HairlineCard>
        )}

        {row && (
          <>
            <HairlineCard sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('gov.admin.contracts.termsSection')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  columnGap: 3,
                  rowGap: 2.5,
                }}
              >
                <Field label={t('gov.admin.contracts.company')} value={row.companyName} />
                <Field
                  label={t('gov.admin.contracts.localGovernment')}
                  value={row.localGovernmentName}
                />
                <Field label={t('gov.admin.contracts.reference')} value={row.reference} />
                <Field
                  label={t('gov.admin.contracts.officers')}
                  value={String(row.appointmentCount)}
                />
                <Field
                  label={t('gov.admin.contracts.startsOn')}
                  value={row.startsOn ? formatDate(row.startsOn, i18n.language) : null}
                />
                <Field
                  label={t('gov.admin.contracts.endsOn')}
                  value={
                    row.endsOn
                      ? formatDate(row.endsOn, i18n.language)
                      : t('gov.admin.contracts.openEnded')
                  }
                />
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Field label={t('gov.admin.contracts.notes')} value={row.notes} />
                </Box>
              </Box>
            </HairlineCard>

            <HairlineCard sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('gov.admin.contracts.authoritySection')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  columnGap: 3,
                  rowGap: 2.5,
                }}
              >
                {/* The resolution, not the signatory, is what binds the
                    municipality — `signedByName` records who entered it. */}
                <Field
                  label={t('gov.admin.contracts.resolution')}
                  value={row.councilResolutionRef}
                />
                <Field label={t('gov.admin.contracts.signedBy')} value={row.signedByName} />
                <Field
                  label={t('gov.admin.contracts.signedAt')}
                  value={row.signedAt ? formatDate(row.signedAt, i18n.language) : null}
                />
              </Box>
            </HairlineCard>

            <HairlineCard sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('gov.admin.contracts.document')}
              </Typography>
              {row.documentFilename ? (
                <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'center' }}>
                  <Icons.FileText size={iconSize.md} aria-hidden />
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
                    {row.documentFilename}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Icons.Download size={iconSize.md} />}
                    onClick={() => {
                      void openContractDocument(row.id, row.localGovernmentId).catch((err) =>
                        toast.error(err, 'gov.admin.contracts.documentFailed'),
                      );
                    }}
                  >
                    {t('gov.admin.contracts.viewDocument')}
                  </Button>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('gov.admin.contracts.noDocumentHelp')}
                </Typography>
              )}
            </HairlineCard>
          </>
        )}
      </Stack>
    </AppShell>
  );
}
